import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConcerts, useCreateConcert } from '@/hooks/useConcerts'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { ConcertsTable, type ConcertFilter } from '@/components/features/concerts/ConcertsTable'
import {
  AddConcertDialog,
  type AddConcertFormData,
} from '@/components/features/concerts/AddConcertDialog'
import { Button } from '@brent/core-ui/ui'
import { Plus } from 'lucide-react'

export function Concerts() {
  const { data: concerts, isLoading, error } = useConcerts()
  const { canEdit } = useAuth()
  const createConcert = useCreateConcert()
  const { toast } = useToast()
  const navigate = useNavigate()
  const [filter, setFilter] = useState<ConcertFilter>('all')
  const [showAddDialog, setShowAddDialog] = useState(false)

  const handleCreateConcert = async (data: AddConcertFormData) => {
    try {
      const result = await createConcert.mutateAsync(data)
      toast({
        title: 'Concert created',
        description: `Created concert: ${data.date_text}`,
      })
      setShowAddDialog(false)
      navigate(`/concerts/${result.id}`)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create concert',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading concerts...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-6">
        <h2 className="font-semibold text-destructive">Failed to load concerts</h2>
        <p className="mt-1 text-sm text-muted-foreground">{error.message}</p>
      </div>
    )
  }

  if (!concerts || concerts.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Concerts</h1>
            <p className="text-muted-foreground mt-2">Browse concert history by decade.</p>
          </div>
          {canEdit && (
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Concert
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground">No concerts found.</p>
        <AddConcertDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          onSubmit={handleCreateConcert}
          isPending={createConcert.isPending}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Concerts</h1>
          <p className="text-muted-foreground mt-2">
            Browse concert history by decade. Click a row to view details.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowAddDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Concert
          </Button>
        )}
      </div>
      <ConcertsTable concerts={concerts} filter={filter} onFilterChange={setFilter} />
      <AddConcertDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onSubmit={handleCreateConcert}
        isPending={createConcert.isPending}
      />
    </div>
  )
}
